import { motion } from "framer-motion";
import { GraduationCap, Heart, Users, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import WordFadeIn from "@/components/WordFadeIn";
import DrawIcon from "@/components/DrawIcon";
import Seo from "@/components/Seo";

const perks = [
  { icon: Heart, title: "Familiäre Atmosphäre", desc: "Persönliches Miteinander in einem 26-köpfigen Team mit starkem Zusammenhalt." },
  { icon: Users, title: "Ausbildungsbetrieb", desc: "Zertifizierter IHK/HWK-Ausbildungsbetrieb – auch Auszubildende sind Teil des Teams." },
  { icon: GraduationCap, title: "Ausbildungsberufe", desc: "Wir bilden aus zum Kaufmann im Einzelhandel und zum Verkäufer (m/w/d)." },
  { icon: Sparkles, title: "Erfahrenes Umfeld", desc: "Über 40 Jahre Handelserfahrung als solide Grundlage für Ihre Entwicklung." },
];

const teamImgs = [
  { src: "/images/regal-team.webp", alt: "Team von Rewe Kotlarski im Markt", caption: "Unser Team" },
  { src: "/images/fuehrungskraefte.webp", alt: "Führungskräfte von Rewe Kotlarski im Regal", caption: "Führungskräfte" },
];

const Karriere = () => (
  <>
      <Seo title="Karriere & Ausbildung | Rewe Kotlarski Meerbusch" description="Ausbildung und Jobs bei Rewe Kotlarski in Meerbusch-Osterath: familiäres Team, persönliche Einarbeitung und Perspektiven im Lebensmitteleinzelhandel." />
    <PageHeader
      tag="Karriere"
      title="Arbeiten bei Rewe Kotlarski"
      subtitle="Informieren Sie sich über Arbeit und Ausbildung bei Rewe Kotlarski. Aktuell sind keine offenen Stellen benannt."
      heroImage="/images/regal-antonella.webp"
      drawIconVariant="star"
      objectPosition="right 25%"
    />

    {/* Perks */}
    <section className="relative py-20 px-6 md:px-16 bg-secondary/30 overflow-hidden">
      <DrawIcon
        variant="star"
        size={160}
        delay={0.5}
        className="absolute right-8 md:right-16 top-8 opacity-15 hidden md:block"
      />
      <div className="max-w-6xl mx-auto relative z-10">
        <WordFadeIn
          text="Was wir bieten"
          as="h2"
          className="font-display text-3xl md:text-5xl font-bold text-foreground text-center md:text-left mb-16 [hyphens:none]"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {perks.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className="bg-card border border-border p-8 flex flex-col items-center text-center md:items-start md:text-left h-full"
            >
              <p.icon className="w-8 h-8 text-primary-bright mb-5 shrink-0" aria-hidden="true" />
              <h3 className="font-display text-xl font-semibold text-foreground mb-2" style={{ textWrap: "pretty" as any }}>{p.title}</h3>
              <p className="text-muted-foreground font-body leading-relaxed" style={{ textWrap: "pretty" as any }}>{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Team impressions */}
    <section className="py-20 px-6 md:px-16">
      <div className="max-w-7xl mx-auto">
        <p className="font-body text-sm text-primary-bright tracking-[0.3em] uppercase mb-4 text-center md:text-left">
          Unser Team
        </p>
        <WordFadeIn
          text="Menschen hinter dem Markt"
          as="h2"
          className="font-display text-3xl md:text-5xl font-bold text-foreground text-center md:text-left mb-16 [hyphens:none]"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {teamImgs.map((t, i) => (
            <motion.figure
              key={t.alt}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 * i }}
              className="relative h-[40vh] md:h-[48vh] overflow-hidden"
            >
              <img
                src={t.src}
                alt={t.alt}
                className="absolute inset-0 w-full h-full object-cover object-center"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/85 via-background/25 to-transparent" />
              <figcaption className="relative z-10 flex items-end h-full p-6">
                <span className="font-display text-base md:text-lg font-medium text-foreground [hyphens:none]">{t.caption}</span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>

    {/* Open Positions */}
    <section className="py-20 px-6 md:px-16">
      <div className="max-w-5xl mx-auto">
        <WordFadeIn
          text="Aktuelle Stellen"
          as="h2"
          className="font-display text-3xl md:text-5xl font-bold text-foreground text-center md:text-left mb-16 [hyphens:none]"
        />
        <div className="border border-border p-8 md:p-12 text-center md:text-left space-y-4">
          <p className="text-muted-foreground font-body text-lg leading-relaxed" style={{ textWrap: "pretty" as any }}>
            Sie möchten Teil des Teams von Rewe Kotlarski werden? Derzeit sind keine offenen Stellen verfügbar. Sobald sich neue Einstiegsmöglichkeiten ergeben, informieren wir Sie an dieser Stelle.
          </p>
          <p className="text-muted-foreground font-body leading-relaxed" style={{ textWrap: "pretty" as any }}>
            Als IHK/HWK-Ausbildungsbetrieb bilden wir aus zum Kaufmann im Einzelhandel und zum Verkäufer (m/w/d).
          </p>
          <a
            href="mailto:info@rewe-kotlarski.de?subject=Initiativbewerbung"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 font-display text-sm font-medium tracking-wide hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
          >
            Initiativbewerbung senden
          </a>
        </div>
      </div>
    </section>
  </>
);

export default Karriere;
